import isEqual from 'lodash/isEqual';
import isArray from 'lodash/isArray';
import isObject from 'lodash/isObject';
import forEach from 'lodash/forEach';

const FEATURE_TYPE = 'Feature';
const FEATURE_COLLECTION_TYPE = 'FeatureCollection';

/**
 * @param {object} obj
 * @param {string[]|string} type
 * @returns {boolean}
 */
function checkObjectType(obj, type) {
	if (!isObject(obj)) {
		return false;
	}

	if (isArray(type)) {
		return type.indexOf(obj.type) > -1;
	} else {
		return obj.type === type;
	}
}

/**
 * @param {object} item
 * @returns {string}
 */
function getCollectionItemIdentifier(item) {
	return item.id;
}

/**
 * Either equal or replace with new object
 *
 * @param {object} input
 * @param {object} output
 * @param {import('./pointer').Pointer} pointer
 * @returns {Array} operations
 */
function diffAtomicPlainObjects(input, output, pointer) {
	return isEqual(input, output)
		? []
		: [
				{
					op: 'replace',
					path: pointer.toString(),
					value: output,
				},
		  ];
}

/**
 *
 * @param {object} input
 * @param {object} output
 * @param {import('./pointer').Pointer} pointer
 * @returns {*[]} operations
 */
function diffFeatures(input, output, pointer) {
	if (!checkObjectType(input, FEATURE_TYPE)) {
		throw new Error('Input should be feature!');
	}

	if (!checkObjectType(output, FEATURE_TYPE)) {
		throw new Error('Output should be feature!');
	}

	if (isEqual(input, output)) {
		return [];
	}

	return [].concat(
		diffAtomicPlainObjects(
			input.properties,
			output.properties,
			pointer.add('properties')
		),
		diffAtomicPlainObjects(
			input.geometry,
			output.geometry,
			pointer.add('geometry')
		)
	);
}

/**
 *
 * @param {Array} input
 * @param {Array} output
 * @param {import('./pointer').Pointer} pointer
 * @returns {*[]} operations
 */
function diffCollectionArrays(input, output, pointer) {
	if (!isArray(input)) {
		throw new Error('Input should be array!');
	}

	if (!isArray(output)) {
		throw new Error('Output should be array!');
	}

	let operations = [
		{
			op: 'removeUnidentifiableFromCollection',
			path: pointer.toString(),
		},
	];
	const inputIdentifiedCollectionItems = {};

	forEach(input, (inputItem) => {
		const itemIdentifier = getCollectionItemIdentifier(inputItem);
		if (itemIdentifier) {
			inputIdentifiedCollectionItems[itemIdentifier] = inputItem;
		}
	});

	forEach(output, (outputItem) => {
		const itemIdentifier = getCollectionItemIdentifier(outputItem);

		// eslint-disable-next-line no-prototype-builtins
		if (
			itemIdentifier &&
			inputIdentifiedCollectionItems.hasOwnProperty(itemIdentifier)
		) {
			const inputItem = inputIdentifiedCollectionItems[itemIdentifier];
			operations = operations.concat(
				diffFeatures(
					inputItem,
					outputItem,
					pointer.add('@' + itemIdentifier)
				)
			);
			delete inputIdentifiedCollectionItems[itemIdentifier];
		} else {
			operations.push({
				op: 'addToCollection',
				path: pointer.toString(),
				item: outputItem,
			});
		}
	});

	forEach(inputIdentifiedCollectionItems, (item) => {
		const itemIdentifier = getCollectionItemIdentifier(item);

		operations.push({
			op: 'removeFromCollection',
			path: pointer.add('@' + itemIdentifier).toString(),
			item: item,
		});
	});

	return operations;
}

/**
 *
 * @param {*} input
 * @param {*} output
 * @param {import('./pointer').Pointer} pointer
 * @returns {*[]} operations
 */
export default function diff(input, output, pointer) {
	if (checkObjectType(input, FEATURE_COLLECTION_TYPE)) {
		return [].concat(
			diffCollectionArrays(
				input.features,
				output.features,
				pointer.add('features')
			),
			diffAtomicPlainObjects(input.crs, output.crs, pointer.add('crs'))
		);
	}

	if (checkObjectType(input, FEATURE_TYPE)) {
		return diffFeatures(input, output, pointer);
	}

	return diffCollectionArrays(input, output, pointer);
}
